import React from 'react';
import { Section } from '@/pages/LandingComplete';
import SectionHeader from './SectionHeader';
import { CheckCircle, Laptop2, Users } from 'lucide-react';

interface ServiceCard {
  badge?: string;
  badgeColor?: string;
  title: string;
  description: string;
  items: string[];
  image?: string;
  imageAlt?: string;
}

interface ServicesContent {
  title?: string;
  titleHighlight?: string;
  subtitle?: string;
  cards?: ServiceCard[];
}

export function DynamicServices({ content }: { content: ServicesContent }) {
  const cards = content.cards || [];
  const hoverBorders = [
    'hover:border-primary/30',
    'hover:border-emerald-500/40',
  ];
  const badgeColors = [
    'bg-primary/10 border border-primary/20 text-primary',
    'bg-emerald-500/10 border border-emerald-500/25 text-emerald-700',
  ];
  const checkColors = ['text-primary', 'text-emerald-600'];
  const icons = [Laptop2, Users];

  return (
    <Section id="leistungen">
      <SectionHeader 
        number="02" 
        title={content.title || "Unsere"} 
        highlight={content.titleHighlight || "Leistungen"}
        subtitle={content.subtitle || "Maßgeschneiderte IT-Unterstützung für Ihren Bedarf"}
      />

      <div className="grid items-stretch gap-8 lg:grid-cols-2">
        {cards.map((card, i) => {
          const Icon = icons[i % icons.length];
          return (
            <div key={card.title + i} className="group h-full">
              <div className={`h-full flex flex-col rounded-[2rem] border border-navy/10 bg-card p-8 shadow-soft transition-all duration-500 hover:shadow-lift ${hoverBorders[i % hoverBorders.length]}`}>
                <div className="mb-6">
                  <div className={`inline-flex items-center gap-2 rounded-full ${badgeColors[i % badgeColors.length]} px-4 py-2 text-sm font-bold mb-4`}>
                    <Icon size={16} />
                    {card.badge}
                  </div>
                  <h3 className="text-2xl font-bold text-navy mb-3">{card.title}</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">{card.description}</p>
                </div>

                <ul className="space-y-4 mb-8">
                  {card.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-3 text-foreground/85">
                      <CheckCircle size={20} className={`mt-0.5 shrink-0 ${checkColors[i % checkColors.length]}`} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                {card.image && (
                  <div className="relative overflow-hidden rounded-2xl shadow-soft group-hover:shadow-lift transition-shadow mt-auto">
                    <img
                      src={card.image}
                      alt={card.imageAlt || card.title}
                      loading="lazy"
                      className="h-64 w-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/40 via-transparent to-transparent"></div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}
